import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { markOnboardingTask } from "../lib/onboarding";
import { MockBadge, Button, Input, Spinner, ZoneChip, LageBadge } from "../components/ui";
import { Bell, Plus, Trash2, Play, X, ChevronRight, Clock, Hammer } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import SearchHistoryModal from "../components/SearchHistoryModal";
import { useI18n } from "../i18n/I18nContext";

// Kanonische Marktliste — mit invest_config.MARKETS synchron halten (55 Märkte).
// Märkte ohne aktuelle Listings müssen wählbar bleiben, darum bewusst hardcoded
// und NICHT aus der Live-/markets-API (die liefert nur Märkte mit Daten).
// Die Codes landen als criteria.markets in der Saved Search und werden
// backend-seitig gegen listing.market_code gematcht — also exakt die
// invest_config-Schlüssel verwenden (z. B. FRANKFURT, nicht FRA).
const MARKETS = [
  // AT
  "STP", "WN", "KLU", "GRAZ", "GRAZ_UM", "LINZ", "WELS", "LEOBEN", "VILLACH",
  "SALZBURG", "INNSBRUCK", "KUFSTEIN", "KREMS", "BADEN", "MOEDLING",
  "KORNEUBURG", "TULLN", "AMSTETTEN", "BREGENZ", "DORNBIRN", "STEYR",
  "VOECKLABRUCK", "WOLFSBERG", "STVEIT", "SPITTAL", "HERMAGOR", "STANDRAE",
  "FELDKIRCHEN",
  // Wien (alle 23 Bezirke)
  "WIEN01", "WIEN02", "WIEN03", "WIEN04", "WIEN05", "WIEN06", "WIEN07",
  "WIEN08", "WIEN09", "WIEN10", "WIEN11", "WIEN12", "WIEN13", "WIEN14",
  "WIEN15", "WIEN16", "WIEN17", "WIEN18", "WIEN19", "WIEN20", "WIEN21",
  "WIEN22", "WIEN23",
  // DE
  "FRANKFURT", "HAMBURG", "LEIPZIG", "ERFURT",
];

export default function Alerts() {
  const { t } = useI18n();
  const [searches, setSearches] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [historyId, setHistoryId] = useState(null);
  const [err, setErr] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();

  const refresh = async () => {
    setErr(null);
    try {
      const [s, n] = await Promise.all([
        api.get("/searches"),
        api.get("/notifications"),
      ]);
      setSearches(s.data.searches);
      setNotifications(n.data.notifications);
    } catch (e) {
      console.error("Alerts load failed", e);
      setErr(t("common.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runPipeline = async () => {
    setBusy(true);
    try { await api.post("/pipeline/run"); await refresh(); }
    finally { setBusy(false); }
  };

  const deleteSearch = async (id) => {
    await api.delete(`/searches/${id}`);
    refresh();
  };

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    refresh();
  };

  const markAllRead = async () => {
    await api.post(`/notifications/read-all`);
    refresh();
  };

  // Flip-Screen preset (CONCEPT-FF-RECHNER.md §8.2.2): renovation keywords the
  // screen already detects (invest_config SANIERUNG_KEYWORDS / _CLEAN_RENO_KW),
  // matched against title + flags + description by the pipeline matcher.
  const createFlipScreen = async () => {
    setBusy(true);
    try {
      await api.post("/searches", {
        name: t("alerts.flipPresetName"),
        criteria: {
          keywords: [
            "renovierungsbedürftig", "sanierungsbedürftig", "sanierungsobjekt",
            "sanierungsbedarf", "bastler", "rohdiamant", "entkern", "ausbaubar",
            "entwicklungspotenzial", "teilbar", "zum sanieren",
          ],
          min_size: 30,
          max_size: 90,
        },
      });
      await refresh();
    } finally { setBusy(false); }
  };

  const unread = notifications.filter((n) => !n.read).length;

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">{t("alerts.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("alerts.title")}</h1>
          <p className="mt-2 text-xs text-text-muted">
            {t("alerts.countLabel", { saved: searches.length, unread })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user?.role === "admin" && (
            <Button onClick={runPipeline} variant="ghost" data-testid="run-pipeline-btn" disabled={busy}>
              {busy ? <Spinner /> : <Play size={14} />} {busy ? t("alerts.running") : t("alerts.runPipelineNow")}
            </Button>
          )}
          <Button onClick={() => setShowForm(true)} variant="green" data-testid="new-search-btn">
            <Plus size={14} /> {t("alerts.newSearch")}
          </Button>
          <Button onClick={createFlipScreen} variant="ghost" data-testid="flip-preset-btn" disabled={busy}
                  title={t("alerts.flipPresetTitle")}>
            <Hammer size={14} /> {t("alerts.flipPreset")}
          </Button>
          <MockBadge />
        </div>
      </div>

      {loading ? <div className="p-10"><Spinner /></div> : err ? (
        <div className="p-10 text-center">
          <p className="text-sm text-signal-red font-mono">{err}</p>
          <button onClick={refresh} className="mt-3 text-xs font-mono uppercase tracking-widest2 text-signal-green hover:underline">
            {t("common.retry")}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-12">
          {/* SEARCHES */}
          <section className="col-span-12 lg:col-span-5 border-r border-border p-6">
            <div className="overline">{t("alerts.savedSearches")}</div>
            <h3 className="mt-1 font-heading text-2xl font-bold">{t("alerts.myCriteria")}</h3>
            {searches.length === 0 ? (
              <div className="mt-6 border border-border p-6 text-center">
                <Bell size={24} className="mx-auto text-text-muted" />
                <p className="mt-3 text-xs font-mono text-text-secondary">
                  {t("alerts.noSearchesYet")}
                </p>
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                {searches.map((s) => (
                  <SearchRow key={s.id} s={s} onDelete={() => deleteSearch(s.id)} onHistory={() => setHistoryId(s.id)} t={t} />
                ))}
              </div>
            )}
          </section>

          {/* NOTIFICATIONS */}
          <section className="col-span-12 lg:col-span-7 p-6">
            <div className="flex items-end justify-between">
              <div>
                <div className="overline">{t("alerts.inbox")}</div>
                <h3 className="mt-1 font-heading text-2xl font-bold">
                  {t("alerts.alertsHeader")} {unread > 0 && <span className="num text-signal-green ml-2">{t("alerts.newCount", { count: unread })}</span>}
                </h3>
              </div>
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  data-testid="mark-all-read"
                  className="font-mono text-[10px] uppercase tracking-widest2 text-text-muted hover:text-white"
                >
                  {t("alerts.markAllRead")}
                </button>
              )}
            </div>
            {notifications.length === 0 ? (
              <div className="mt-6 border border-border p-6 text-center">
                <Bell size={24} className="mx-auto text-text-muted" />
                <p className="mt-3 text-xs font-mono text-text-secondary">
                  {t("alerts.noAlertsYet")}
                </p>
              </div>
            ) : (
              <div className="mt-4 border border-border">
                {notifications.map((n) => {
                  const snap = n.listing_snapshot || n.listing || {};
                  const isRental = !!snap.unit_key;
                  return (
                    <button
                      key={n.id}
                      onClick={() => {
                        markRead(n.id);
                        nav(isRental ? `/mieten?focus=${encodeURIComponent(snap.unit_key)}` : `/listings/${n.listing_id}`);
                      }}
                      data-testid={`notification-${n.id}`}
                      className={`flex w-full items-center gap-4 p-4 border-b border-border last:border-b-0 text-left transition-colors ${
                        n.read ? "bg-bg hover:bg-surfaceHover" : "bg-signal-green/5 hover:bg-signal-green/10"
                      }`}
                    >
                      {isRental ? (
                        <>
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-mono text-text-muted">
                              {n.search_name}
                              <span className="ml-2 text-signal-green">·{t("alerts.rentalTag")}</span>
                              {snap.delta_pct != null && (
                                <span className={`ml-2 ${snap.delta_pct < 0 ? "text-signal-green" : "text-signal-red"}`}>
                                  ·{snap.delta_pct > 0 ? "+" : ""}{snap.delta_pct}%
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-white truncate">{snap.title || snap.market}</div>
                            <div className="text-[10px] font-mono text-text-muted">
                              {snap.market} · {new Date(n.created_at).toLocaleString("en-GB")}
                            </div>
                          </div>
                          <div className="text-right">
                            {snap.price != null && <div className="num text-lg">€{Math.round(snap.price).toLocaleString("de-AT")}</div>}
                            {snap.deal_score != null && (
                              <div className="num text-xs text-signal-green" data-testid={`score-chip-${n.id}`}>Score {snap.deal_score}</div>
                            )}
                            {snap.mrg_savings_mo > 0 && (
                              <div className="text-[10px] font-mono text-signal-yellow">{t("alerts.potenzial", { amount: Math.round(snap.mrg_savings_mo) })}</div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex flex-col items-center gap-1 min-w-[60px]">
                            <ZoneChip zone={n.listing.zone} />
                            <LageBadge grade={n.listing.lage_grade} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-mono text-text-muted">
                              {n.search_name}
                              {n.listing.is_new && <span className="ml-2 text-signal-green">·NEW</span>}
                              {n.listing.price_drop_pct && <span className="ml-2 text-signal-yellow">·−{n.listing.price_drop_pct}%</span>}
                            </div>
                            <div className="text-sm text-white truncate">{n.listing.title || n.listing.market_name}</div>
                            <div className="text-[10px] font-mono text-text-muted">{new Date(n.created_at).toLocaleString("en-GB")}</div>
                          </div>
                          <div className="text-right">
                            <div className="num text-lg">{n.listing.faktor}</div>
                            <div className="num text-xs text-text-muted">€{(n.listing.price/1000).toFixed(0)}k</div>
                          </div>
                        </>
                      )}
                      <ChevronRight size={14} className="text-text-muted" />
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      )}

      {showForm && (
        <NewSearchModal
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); refresh(); }}
        />
      )}
      {historyId && (
        <SearchHistoryModal searchId={historyId} onClose={() => setHistoryId(null)} />
      )}
    </div>
  );
}

function SearchRow({ s, onDelete, onHistory, t }) {
  const [matches, setMatches] = useState(null);
  const test = async () => {
    const { data } = await api.post(`/searches/${s.id}/test`);
    setMatches(data.total);
  };
  useEffect(() => { test(); /* eslint-disable-line */ }, []);
  const c = s.criteria || {};
  const chips = [];
  if (c.markets?.length) chips.push(t("alerts.marketCount", { count: c.markets.length, unit: c.markets.length > 1 ? t("alerts.marketMany") : t("alerts.marketOne") }));
  if (c.zones?.length) chips.push(c.zones.join("/"));
  if (c.grades?.length) chips.push(t("alerts.lagePrefix", { grades: c.grades.join("/") }));
  if (c.max_faktor) chips.push(t("alerts.faktorMax", { max: c.max_faktor }));
  if (c.max_price) chips.push(t("alerts.priceMax", { price: (c.max_price/1000).toFixed(0) }));
  if (c.min_size || c.max_size) chips.push(`${c.min_size || 0}-${c.max_size || "∞"} m²`);

  return (
    <div className="border border-border bg-surface p-4" data-testid={`search-row-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-heading font-bold text-base">{s.name}</div>
            <span
              data-testid="search-kind-tag"
              className="text-[9px] font-mono uppercase tracking-widest2 border border-border text-text-secondary px-1.5 py-0.5"
            >
              {c.deal_type === "miete" ? t("alerts.kindMiete") : t("alerts.kindKauf")}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {chips.map((c, i) => (
              <span key={i} className="text-[10px] font-mono border border-border text-text-secondary px-1.5 py-0.5">
                {c}
              </span>
            ))}
            {chips.length === 0 && <span className="text-[10px] font-mono text-text-muted">{t("alerts.noFilters")}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="num text-2xl text-signal-green">{matches ?? "—"}</div>
          <div className="overline">{t("alerts.matches")}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[10px] font-mono text-text-muted">
          {t("alerts.since", { date: new Date(s.created_at).toLocaleDateString("en-GB") })}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={onHistory}
            data-testid={`history-search-${s.id}`}
            className="text-text-muted hover:text-white hover:bg-surfaceHover px-2 py-1 text-[10px] font-mono uppercase tracking-widest2"
          >
            <Clock size={12} className="inline mr-1" /> {t("alerts.history")}
          </button>
          <button
            onClick={onDelete}
            data-testid={`delete-search-${s.id}`}
            className="text-signal-red hover:bg-signal-red/10 px-2 py-1 text-[10px] font-mono uppercase tracking-widest2"
          >
            <Trash2 size={12} className="inline mr-1" /> {t("alerts.delete")}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewSearchModal({ onClose, onSaved }) {
  const { t } = useI18n();
  const [kind, setKind] = useState("kauf");
  const [name, setName] = useState(t("alerts.defaultName"));
  const [markets, setMarkets] = useState([]);
  const [zones, setZones] = useState(["GREEN", "GREEN_BORDER"]);
  const [maxFaktor, setMaxFaktor] = useState("22");
  const [maxPrice, setMaxPrice] = useState("");
  const [minSize, setMinSize] = useState("");
  const [maxSize, setMaxSize] = useState("");
  const [maxKalt, setMaxKalt] = useState("");
  const [minRooms, setMinRooms] = useState("");
  const [underMedian, setUnderMedian] = useState("");
  const [segment, setSegment] = useState("");
  const [befristet, setBefristet] = useState("");
  const [mrgOnly, setMrgOnly] = useState(false);
  const [minScore, setMinScore] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const criteria = {};
      if (markets.length) criteria.markets = markets;
      if (kind === "miete") {
        criteria.deal_type = "miete";
        if (maxPrice) criteria.max_price = parseFloat(maxPrice);
        if (maxKalt) criteria.max_kalt = parseFloat(maxKalt);
        if (minSize) criteria.min_size = parseFloat(minSize);
        if (maxSize) criteria.max_size = parseFloat(maxSize);
        if (minRooms) criteria.min_rooms = parseFloat(minRooms);
        if (underMedian) criteria.under_median = parseFloat(underMedian);
        if (segment) criteria.segment = segment;
        if (befristet !== "") criteria.befristet = befristet === "true";
        if (mrgOnly) criteria.mrg_only = true;
        if (minScore) criteria.min_score = parseInt(minScore, 10);
      } else {
        if (zones.length) criteria.zones = zones;
        if (maxFaktor) criteria.max_faktor = parseFloat(maxFaktor);
        if (maxPrice) criteria.max_price = parseFloat(maxPrice);
        if (minSize) criteria.min_size = parseFloat(minSize);
        if (maxSize) criteria.max_size = parseFloat(maxSize);
      }
      await api.post("/searches", { name, criteria });
      markOnboardingTask("alert");
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onClose}
        aria-label={t("common.close")}
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
      />
      <div className="fixed inset-0 z-50 grid place-items-center p-4 pointer-events-none">
        <form
          onSubmit={save}
          className="border border-border bg-bg w-full max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto reveal"
          data-testid="new-search-modal"
        >
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div>
              <div className="overline">{t("alerts.savedSearch")}</div>
              <h3 className="font-heading text-2xl font-bold mt-1">{t("alerts.newDealAlert")}</h3>
            </div>
            <button type="button" onClick={onClose} className="text-text-muted hover:text-white p-2">
              <X size={18} />
            </button>
          </div>

          <div className="p-5 space-y-5">
            <div>
              <label className="overline">{t("alerts.nameField")}</label>
              <Input
                data-testid="search-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1"
              />
            </div>

            <div>
              <label className="overline">{t("alerts.kindField")}</label>
              <div className="mt-2 flex gap-2">
                {["kauf", "miete"].map((k) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`search-kind-${k}`}
                    onClick={() => setKind(k)}
                    className={`px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest2 border transition-colors ${
                      kind === k
                        ? "border-signal-green text-signal-green bg-signal-green/10"
                        : "border-border text-text-muted hover:border-zinc-500"
                    }`}
                  >
                    {k === "kauf" ? t("alerts.kindKauf") : t("alerts.kindMiete")}
                  </button>
                ))}
              </div>
            </div>

            {kind === "kauf" && (
            <div>
              <label className="overline">{t("alerts.zonesField")}</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {["GREEN", "GREEN_BORDER", "YELLOW", "RED"].map((z) => (
                  <button
                    key={z}
                    type="button"
                    onClick={() => toggle(zones, setZones, z)}
                    className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest2 border transition-colors ${
                      zones.includes(z)
                        ? "border-signal-green text-signal-green bg-signal-green/10"
                        : "border-border text-text-muted hover:border-zinc-500"
                    }`}
                  >
                    {z}
                  </button>
                ))}
              </div>
            </div>
            )}

            <div>
              <label className="overline">{t("alerts.marketsField", { count: markets.length })}</label>
              <div className="mt-2 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 border border-border bg-surface">
                {MARKETS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    data-testid={`market-chip-${m}`}
                    onClick={() => toggle(markets, setMarkets, m)}
                    className={`px-2 py-1 font-mono text-[10px] uppercase tracking-widest2 border transition-colors ${
                      markets.includes(m)
                        ? "border-white text-white bg-white/10"
                        : "border-border text-text-muted hover:border-zinc-500"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <div className="mt-1 text-[10px] font-mono text-text-muted">{t("alerts.leaveEmptyMarkets")}</div>
            </div>

            {kind === "kauf" ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline">{t("alerts.maxFaktorField")}</label>
                <Input data-testid="search-max-faktor" type="number" step="0.1" value={maxFaktor} onChange={(e) => setMaxFaktor(e.target.value)} className="mt-1" />
              </div>
              <div>
                <label className="overline">{t("alerts.maxPriceField")}</label>
                <Input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="mt-1" placeholder={t("common.optional")} />
              </div>
              <div>
                <label className="overline">{t("alerts.minSizeField")}</label>
                <Input type="number" step="1" value={minSize} onChange={(e) => setMinSize(e.target.value)} className="mt-1" placeholder={t("common.optional")} />
              </div>
              <div>
                <label className="overline">{t("alerts.maxSizeField")}</label>
                <Input type="number" step="1" value={maxSize} onChange={(e) => setMaxSize(e.target.value)} className="mt-1" placeholder={t("common.optional")} />
              </div>
            </div>
            ) : (
            <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline">{t("alerts.maxPriceField")}</label>
                <Input data-testid="miete-max-price" type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="mt-1" placeholder={t("common.optional")} />
              </div>
              <div>
                <label className="overline">{t("alerts.maxKaltField")}</label>
                <Input data-testid="miete-max-kalt" type="number" value={maxKalt} onChange={(e) => setMaxKalt(e.target.value)} className="mt-1" placeholder={t("common.optional")} />
              </div>
              <div>
                <label className="overline">{t("alerts.minSizeField")}</label>
                <Input data-testid="miete-min-size" type="number" step="1" value={minSize} onChange={(e) => setMinSize(e.target.value)} className="mt-1" placeholder={t("common.optional")} />
              </div>
              <div>
                <label className="overline">{t("alerts.maxSizeField")}</label>
                <Input data-testid="miete-max-size" type="number" step="1" value={maxSize} onChange={(e) => setMaxSize(e.target.value)} className="mt-1" placeholder={t("common.optional")} />
              </div>
              <div>
                <label className="overline">{t("alerts.minRoomsField")}</label>
                <Input data-testid="miete-min-rooms" type="number" step="0.5" value={minRooms} onChange={(e) => setMinRooms(e.target.value)} className="mt-1" placeholder={t("common.optional")} />
              </div>
              <div>
                <label className="overline">{t("alerts.minScoreField")}</label>
                <Input data-testid="miete-min-score" type="number" step="1" value={minScore} onChange={(e) => setMinScore(e.target.value)} className="mt-1" placeholder={t("common.optional")} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="overline">{t("alerts.underMedianField")}</label>
                <select
                  data-testid="miete-under-median"
                  value={underMedian}
                  onChange={(e) => setUnderMedian(e.target.value)}
                  className="mt-1 w-full bg-surface border border-border px-3 py-2 text-sm text-white font-mono"
                >
                  <option value="">{t("alerts.underMedianAny")}</option>
                  <option value="5">≥ 5 %</option>
                  <option value="10">≥ 10 %</option>
                  <option value="15">≥ 15 %</option>
                </select>
              </div>
              <div>
                <label className="overline">{t("alerts.segmentField")}</label>
                <select
                  data-testid="miete-segment"
                  value={segment}
                  onChange={(e) => setSegment(e.target.value)}
                  className="mt-1 w-full bg-surface border border-border px-3 py-2 text-sm text-white font-mono"
                >
                  <option value="">{t("alerts.segmentAll")}</option>
                  <option value="frei">{t("alerts.segmentFrei")}</option>
                  <option value="gefoerdert">{t("alerts.segmentGefoerdert")}</option>
                </select>
              </div>
              <div>
                <label className="overline">{t("alerts.befristetField")}</label>
                <select
                  data-testid="miete-befristet"
                  value={befristet}
                  onChange={(e) => setBefristet(e.target.value)}
                  className="mt-1 w-full bg-surface border border-border px-3 py-2 text-sm text-white font-mono"
                >
                  <option value="">{t("alerts.befristetAny")}</option>
                  <option value="true">{t("alerts.befristetYes")}</option>
                  <option value="false">{t("alerts.befristetNo")}</option>
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-xs font-mono text-text-secondary cursor-pointer">
              <input
                data-testid="miete-mrg-only"
                type="checkbox"
                checked={mrgOnly}
                onChange={(e) => setMrgOnly(e.target.checked)}
              />
              {t("alerts.mrgOnlyField")}
            </label>
            </>
            )}

            {err && (
              <div className="border border-signal-red/40 bg-signal-red/10 text-signal-red text-xs px-3 py-2 font-mono">
                {err}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
              <Button type="submit" variant="green" disabled={busy} data-testid="save-search-btn">
                {busy ? <Spinner /> : <Plus size={14} />} {t("alerts.saveAlert")}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
